/**
 * A save refused because a special date in it would price no night.
 *
 * The strategy editor's save (`POST /api/config`) and the wizard's step save
 * (`POST /api/onboarding/save-step`) both refuse a special date the engine can
 * never match — "11.10.2026", say, which is how Art Hotel typed eight football
 * matches that then priced nothing (Sep 2026) — with a 400 carrying
 * `SPECIAL_DATE_UNREADABLE` and the dates it named. The screens name the rows
 * from that code rather than printing the server's English sentence, so the
 * refusal travels as itself. A module of its own, not either API client's,
 * because both throw it and the screens' tests replace those clients whole.
 */

/** The code both saves answer with; `specialDatePattern.ts` on the server. */
export const UNREADABLE_SPECIAL_DATE = "SPECIAL_DATE_UNREADABLE";

export class UnreadableSpecialDateError extends Error {
    readonly code = UNREADABLE_SPECIAL_DATE;
    /** The dates the server refused, as they were written. */
    readonly patterns: string[];

    constructor(message: string, patterns: string[]) {
        super(message);
        this.name = "UnreadableSpecialDateError";
        this.patterns = patterns;
    }
}

/**
 * The refusal a response carries, or null when it carries something else.
 * Reads a copy of the body, so the caller can still read the original.
 */
export async function readUnreadableSpecialDateRefusal(res: Response): Promise<UnreadableSpecialDateError | null> {
    if (res.status !== 400) return null;
    const data = await res.clone().json().catch(() => null) as
        { code?: unknown; error?: unknown; patterns?: unknown } | null;
    if (data?.code !== UNREADABLE_SPECIAL_DATE) return null;
    const patterns = Array.isArray(data.patterns)
        ? data.patterns.filter((pattern): pattern is string => typeof pattern === "string")
        : [];
    return new UnreadableSpecialDateError(
        typeof data.error === "string" ? data.error : "A special date was refused.",
        patterns,
    );
}

/** Whether a failed save was this refusal, told by its code rather than its class. */
export function isUnreadableSpecialDateRefusal(err: unknown): boolean {
    return typeof err === "object" && err !== null && (err as { code?: unknown }).code === UNREADABLE_SPECIAL_DATE;
}
